import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const fetchLeads = createAsyncThunk(
  'leads/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.leads.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLeadById = createAsyncThunk(
  'leads/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.leads.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/create',
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await apiService.leads.create(leadData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.leads.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.leads.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLeadStats = createAsyncThunk(
  'leads/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.leads.getStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state shape
const initialState = {
  items: [],
  selectedLead: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  },
  stats: {
    total: 0,
    totalValue: 0,
    byStatus: {},
    bySource: {}
  },
  filters: {
    search: '',
    status: 'all',
    source: 'all',
  },
};

// Create slice
const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    // Synchronous actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedLead: (state) => {
      state.selectedLead = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Fetch all leads
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch lead by ID
    builder
      .addCase(fetchLeadById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedLead = action.payload.data || action.payload;
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create lead
    builder
      .addCase(createLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        const newLead = action.payload.data || action.payload;
        state.items.unshift(newLead);
        state.pagination.total = (state.pagination.total || 0) + 1;
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update lead
    builder
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        const updatedLead = action.payload.data || action.payload;
        const index = state.items.findIndex(item => item._id === updatedLead._id);
        if (index !== -1) {
          state.items[index] = updatedLead;
        }
        if (state.selectedLead?._id === updatedLead._id) {
          state.selectedLead = updatedLead;
        }
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete lead
    builder
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item._id !== action.payload);
        state.pagination.total = Math.max(0, (state.pagination.total || 1) - 1);
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch lead stats
    builder
      .addCase(fetchLeadStats.fulfilled, (state, action) => {
        state.stats = action.payload.data || action.payload;
      });
  }
});

export const { setFilters, clearSelectedLead, clearError, setPagination } = leadsSlice.actions;
export default leadsSlice.reducer;
